import { Coordinates } from '../models/Settings.model';

export interface LocationValidationResult {
  isWithinRadius: boolean;
  distanceInMeters: number;
  radiusInMeters: number;
  userCoords: { latitude: number; longitude: number };
  venueCoords: { latitude: number; longitude: number };
}

/**
 * Calculates great-circle distance between two geographic coordinates using the Haversine formula.
 * @returns distance in meters
 */
export function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's mean radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const deltaPhi = toRad(lat2 - lat1);
  const deltaLambda = toRad(lon2 - lon1);

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(R * c);
}

export function extractCoordinates(coords?: Coordinates | null): { latitude: number; longitude: number } | null {
  if (!coords) return null;
  const lat = typeof coords.latitude === 'number' ? coords.latitude : (typeof coords.lat === 'number' ? coords.lat : null);
  const lng = typeof coords.longitude === 'number' ? coords.longitude : (typeof coords.lng === 'number' ? coords.lng : null);
  if (lat === null || lng === null || isNaN(lat) || isNaN(lng)) return null;
  return { latitude: lat, longitude: lng };
}

/**
 * Requests browser geolocation with a timeout and accuracy options.
 * Falls back to low accuracy if high accuracy times out or is unavailable.
 */
export async function getCurrentPosition(options?: PositionOptions): Promise<GeolocationPosition> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('Geolocation is not supported by this browser.');
  }

  try {
    return await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000,
        ...options,
      });
    });
  } catch (err: any) {
    if (err?.code === 2 || err?.code === 3) {
      return await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 60000,
        });
      });
    }
    throw err;
  }
}
